import type { Acao, Recurso } from '@arena/shared/permissoes';
import type { NomeDeIcone } from '../ui/Icone.js';

/**
 * O menu, derivado da MATRIZ — não de uma lista com `if`.
 *
 * O protótipo esconde item de menu com `data-roles="dono gestor"`. Uma
 * transcrição literal disso seria uma lista de perfis por item, e a
 * promessa de #v-acessos morreria na primeira linha: criar um perfil
 * novo ("estagiário do bar" é o exemplo do próprio protótipo) é
 * CONFIGURAÇÃO, e um menu que compara nomes de perfil não conhece o
 * perfil novo.
 *
 * Aqui cada item declara o par (recurso, ação) que o justifica, e o
 * shell pergunta à matriz da sessão. Um perfil inventado amanhã, com a
 * célula de Agenda preenchida, ganha o item de Agenda sem tocar neste
 * arquivo.
 *
 * Os grupos e a ordem são os do protótipo: Operação · Dinheiro ·
 * Inteligência · Configuração.
 */

export interface ItemDeMenu {
  readonly rota: string;
  readonly rotulo: string;
  readonly icone: NomeDeIcone;
  /** O que a matriz precisa liberar para este item aparecer. */
  readonly exige: { recurso: Recurso; acao: Acao };
}

export interface GrupoDeMenu {
  readonly titulo: string;
  readonly itens: readonly ItemDeMenu[];
}

export const MENU: readonly GrupoDeMenu[] = [
  {
    titulo: 'Operação',
    itens: [
      {
        rota: '/painel',
        rotulo: 'Painel de hoje',
        icone: 'dash',
        exige: { recurso: 'dashboard', acao: 'ler' },
      },
      {
        rota: '/agenda',
        rotulo: 'Agenda das quadras',
        icone: 'cal',
        exige: { recurso: 'agenda', acao: 'ler' },
      },
      {
        rota: '/alunos',
        rotulo: 'Alunos',
        icone: 'users',
        exige: { recurso: 'alunos', acao: 'ler' },
      },
      {
        rota: '/turmas',
        rotulo: 'Turmas e chamada',
        icone: 'ball',
        exige: { recurso: 'turmas', acao: 'ler' },
      },
      {
        rota: '/bar',
        rotulo: 'Bar e comandas',
        icone: 'tag',
        exige: { recurso: 'comanda', acao: 'ler' },
      },
    ],
  },
  {
    titulo: 'Dinheiro',
    itens: [
      {
        rota: '/planos',
        rotulo: 'Planos e cobranças',
        icone: 'money',
        exige: { recurso: 'cobrancas', acao: 'ler' },
      },
      {
        rota: '/financeiro',
        rotulo: 'Financeiro',
        icone: 'chart',
        exige: { recurso: 'financeiro', acao: 'ler' },
      },
      {
        rota: '/estoque',
        rotulo: 'Estoque',
        icone: 'box',
        exige: { recurso: 'estoque', acao: 'ler' },
      },
    ],
  },
  {
    titulo: 'Inteligência',
    itens: [
      {
        rota: '/bi',
        rotulo: 'BI de ocupação',
        icone: 'chart',
        exige: { recurso: 'bi', acao: 'ler' },
      },
    ],
  },
  {
    titulo: 'Configuração',
    itens: [
      {
        rota: '/acessos',
        rotulo: 'Acessos e perfis',
        icone: 'lock',
        exige: { recurso: 'perfis', acao: 'ler' },
      },
      {
        rota: '/auditoria',
        rotulo: 'Console de auditoria',
        icone: 'clip',
        exige: { recurso: 'perfis', acao: 'ler' },
      },
      {
        rota: '/configuracoes',
        rotulo: 'Configurações da arena',
        icone: 'lock',
        // A tela é a mesma trava de quem edita a matriz: ela é onde a
        // arena é administrada, e não há linha própria para ela em
        // `tb-rbac` — "Perfis e permissões" é a mais próxima.
        exige: { recurso: 'perfis', acao: 'editar' },
      },
      {
        rota: '/site',
        rotulo: 'Site e reservas',
        icone: 'globe',
        // O formulário público de reservas cai na agenda: quem cuida
        // dele é quem pode marcar. Ver 14-site-reservas-publicas.md.
        exige: { recurso: 'agenda', acao: 'editar' },
      },
    ],
  },
];

/** Os grupos que sobram para um perfil. Grupo sem item não é exibido. */
export function menuVisivel(pode: (recurso: Recurso, acao: Acao) => boolean): GrupoDeMenu[] {
  return MENU.map((grupo) => ({
    titulo: grupo.titulo,
    itens: grupo.itens.filter((item) => pode(item.exige.recurso, item.exige.acao)),
  })).filter((grupo) => grupo.itens.length > 0);
}

/**
 * A tela em que o perfil cai depois do login: a primeira do menu dele.
 *
 * O Professor não tem o painel executivo — mandá-lo para `/painel`
 * seria abrir, logo na entrada, uma tela que o menu dele esconde. Ele
 * cai na agenda, que é o que ele tem.
 */
export function rotaInicial(pode: (recurso: Recurso, acao: Acao) => boolean): string {
  return menuVisivel(pode)[0]?.itens[0]?.rota ?? '/painel';
}
